import { useCallback, useState } from 'react';

/**
 * useAction runs one mutation at a time for a panel: which action is busy,
 * the last error, and the last success notice — so every ERP panel reports
 * pending, failure and success the same way.
 */
export function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = useCallback(async <T,>(key: string, action: () => Promise<T>, success?: string | ((result: T) => string)) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const result = await action();
      if (success) setNotice(typeof success === 'function' ? success(result) : success);
      return result;
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      return undefined;
    } finally {
      setBusy(null);
    }
  }, []);

  return { busy, error, notice, run, setError, setNotice };
}
